/**
 * AbsorptionPassiveSkillEffect — Poison tree Expel path passive
 * (expel_path_passive).
 *
 * While Expel Mk.II is active, 15% of all poison DoT damage dealt by the
 * player from any source converts to HP. Virulent Touch ticks, Contagion
 * remnants, Corrode clouds — everything with a `poison` status tag
 * contributes. The conversion is small per individual tick but substantial
 * across a sustained fight with many concurrent infections.
 *
 * This node gates Fork 2 for the Expel path (Ecdysis / Corpse Bloom).
 *
 * Integration: the integration layer (or StatusEffectSystem) calls
 * `handlePoisonDotTick(damage)` each time a player-sourced poison DoT tick
 * resolves damage against any enemy. The passive checks whether Expel Mk.II
 * is active and, if so, heals the player for `damage × conversionRate`.
 *
 * IMPORTANT: Absorption is conditioned on Expel Mk.II being *active*, not
 * merely unlocked. The isExpelActive callback must check the toggle state.
 *
 * UpgradeEffectResolver key:
 *   `dotToHealConversion`: value 0.15, scope 'tree', aggregation 'sum'
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Absorption (expel_path_passive)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface AbsorptionPassiveDeps {
  readonly resolver: {
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly player: {
    heal(amount: number): void;
  };
  /** Returns true when Expel Mk.II is currently toggled on. */
  readonly isExpelActive: () => boolean;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AbsorptionPassiveConfig {
  /** Fallback conversion rate if resolver returns 0 (safety net). */
  readonly defaultConversionRate: number;
}

const DEFAULT_CONFIG: AbsorptionPassiveConfig = {
  defaultConversionRate: 0.15,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class AbsorptionPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'expel_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: AbsorptionPassiveDeps;
  private readonly config: AbsorptionPassiveConfig;

  private _totalHealing = 0;
  private _ticksConverted = 0;

  constructor(
    deps: AbsorptionPassiveDeps,
    config: Partial<AbsorptionPassiveConfig> = {},
  ) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  /** Total HP healed through Absorption since creation. */
  get totalHealing(): number { return this._totalHealing; }

  /** Number of DoT ticks that produced healing. */
  get ticksConverted(): number { return this._ticksConverted; }

  /** Current conversion rate from resolver (or fallback config). */
  get conversionRate(): number {
    const resolved = this.deps.resolver.getTreePassiveEffect(
      'poison', 'dotToHealConversion', 'sum',
    );
    return resolved > 0 ? resolved : this.config.defaultConversionRate;
  }

  // --- External event handler ---

  /**
   * Called by the integration layer each time a player-sourced poison DoT
   * tick deals damage to any enemy. If Expel Mk.II is active, the player
   * heals for `damage × conversionRate`.
   *
   * @param damage — the amount of poison damage dealt by this tick
   */
  handlePoisonDotTick(damage: number): void {
    if (damage <= 0) return;
    if (!this.deps.isExpelActive()) return;

    const healAmount = damage * this.conversionRate;
    if (healAmount <= 0) return;

    this.deps.player.heal(healAmount);
    this._totalHealing += healAmount;
    this._ticksConverted++;
  }

  // --- SkillEffect interface (passive — no activation logic) ---

  update(_delta: number): void { /* passive — no per-frame logic */ }

  destroy(): void { /* no resources to release */ }
}
